<script setup>
// Camada de ligações universal: ativa uma sessão de VOZ (2º aparelho do MESMO número)
// em qualquer caixa WhatsApp não-oficial, sem mexer no provider de mensagens.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const state = ref('loading'); // loading | disabled | qr | connecting | open | logged_out | error
const qrDataUrl = ref('');
const isWorking = ref(false);
let pollTimer = null;

const isActive = computed(() => state.value === 'open');
const showQr = computed(() => state.value === 'qr' && qrDataUrl.value);

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const fetchStatus = async () => {
  try {
    const { data } = await InboxesAPI.voiceStatus(props.inbox.id);
    state.value = data.state || 'error';
    qrDataUrl.value = data.qrDataUrl || '';
    // pareou ou desativou: para o poll agressivo
    if (['open', 'disabled', 'error'].includes(state.value)) stopPolling();
  } catch (error) {
    state.value = 'error';
    stopPolling();
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(fetchStatus, 3000);
};

const enableVoice = async () => {
  isWorking.value = true;
  try {
    await InboxesAPI.voiceSetup(props.inbox.id);
    state.value = 'connecting';
    await fetchStatus();
    startPolling();
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const disableVoice = async () => {
  isWorking.value = true;
  try {
    await InboxesAPI.voiceTeardown(props.inbox.id);
    stopPolling();
    state.value = 'disabled';
    qrDataUrl.value = '';
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.DISABLED_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

onMounted(async () => {
  await fetchStatus();
  // sessão existe mas ainda não pareada: segue acompanhando
  if (['qr', 'connecting', 'logged_out'].includes(state.value)) startPolling();
});

onBeforeUnmount(stopPolling);
</script>

<template>
  <!-- some por completo enquanto carrega e em instalações sem o serviço de voz -->
  <SettingsSection
    v-if="!['loading', 'unavailable'].includes(state)"
    :title="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.TITLE')"
    :sub-title="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.SUBTITLE')"
  >
    <div class="flex flex-col gap-3">
      <!-- estado -->
      <div class="flex items-center gap-2 text-sm">
        <span
          class="w-2 h-2 rounded-full"
          :class="isActive ? 'bg-n-teal-9' : 'bg-n-slate-9'"
        />
        <span class="text-n-slate-12">
          {{
            isActive
              ? t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.STATUS_ACTIVE')
              : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.STATUS_INACTIVE')
          }}
        </span>
      </div>

      <!-- QR de pareamento -->
      <div v-if="showQr" class="flex flex-col gap-2">
        <p class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.SCAN_HINT') }}
        </p>
        <img
          :src="qrDataUrl"
          alt="QR"
          class="w-52 h-52 rounded-lg bg-white p-2"
        />
      </div>
      <p
        v-else-if="state === 'connecting'"
        class="text-sm text-n-slate-11 animate-pulse"
      >
        {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.LOADING_QR') }}
      </p>

      <!-- ações -->
      <div class="flex items-center gap-2">
        <NextButton
          v-if="!isActive"
          class="w-fit"
          :is-loading="isWorking"
          :disabled="state === 'loading'"
          @click="enableVoice"
        >
          {{
            showQr || state === 'connecting'
              ? t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.REGENERATE')
              : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.ENABLE')
          }}
        </NextButton>
        <NextButton
          v-if="isActive || showQr || state === 'logged_out'"
          class="w-fit"
          ruby
          faded
          :is-loading="isWorking"
          @click="disableVoice"
        >
          {{ t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_VOICE.DISABLE') }}
        </NextButton>
      </div>
    </div>
  </SettingsSection>
</template>
